import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Check, ChevronLeft, Facebook, Link2, MapPin, MessageCircle, Share2 } from "lucide-react";
import { useApi, img, waLink } from "@/lib/api";
import { formatRange } from "@/lib/image";
import { Seo } from "@/components/site/Seo";
import { ZoomImg } from "@/components/site/Reveal";
import NotFound from "@/pages/NotFound";
import { useLang } from "@/lib/i18n";

const ShareButtons = ({ title }) => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const url = window.location.href.split("#")[0];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = Object.assign(document.createElement("textarea"), { value: url });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const btn = "inline-flex items-center gap-2 rounded-full border border-clay bg-white px-4 py-2 text-sm font-bold text-forest transition-colors duration-200 hover:bg-linen";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="retreat-share">
      <span className="mr-1 flex items-center gap-1.5 text-sm font-bold text-moss"><Share2 className="h-4 w-4" /> {t("retreat.share")}</span>
      {navigator.share && <button type="button" onClick={() => navigator.share({ title, url }).catch(() => {})} className={btn} data-testid="retreat-share-native"><Share2 className="h-4 w-4" /> {t("retreat.shareBtn")}</button>}
      <a href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`} target="_blank" rel="noreferrer" className={btn} data-testid="retreat-share-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className={btn} data-testid="retreat-share-facebook"><Facebook className="h-4 w-4" /> Facebook</a>
      <button type="button" onClick={copy} className={btn} data-testid="retreat-share-copy">{copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? t("retreat.copied") : t("retreat.copy")}</button>
    </div>
  );
};

export default function RetreatDetail() {
  const { slug } = useParams();
  const { t, L, lang, to } = useLang();
  const { data: r, loading, error } = useApi(`/retreats/${slug}`, null);
  if (error) return <NotFound />;
  if (loading || !r) return <div className="min-h-[60vh]" />;
  const title = L(r, "title");
  const digits = (r.whatsapp || "").replace(/\D/g, "");
  const jsonLd = { "@context": "https://schema.org", "@type": "Event", name: r.title, startDate: r.start_date, endDate: r.end_date, image: r.image,
    description: r.description, eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode", location: { "@type": "Place", name: r.venue, address: "Escamequita, Rivas, Nicaragua" } };
  return (
    <>
      <Seo title={`${title} | Retreats in Escamequita, Nicaragua`} description={L(r, "description").slice(0, 155)} image={r.image} jsonLd={jsonLd} />
      <section className="container-x pb-28 pt-10" data-testid="retreat-detail">
        <Link to={to("/retreats")} className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest" data-testid="retreat-back"><ChevronLeft className="h-4 w-4" /> {t("retreat.back")}</Link>
        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <ZoomImg src={img(r.image)} alt={title} className="aspect-[4/3] w-full rounded-2xl object-cover" data-testid="retreat-image" />
          <div>
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-sage" data-testid="retreat-dates"><CalendarDays className="h-4 w-4" /> {formatRange(r.start_date, r.end_date, lang)}</p>
            <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-forest sm:text-5xl" data-testid="retreat-title">{title}</h1>
            <p className="mt-4 flex items-center gap-2 font-semibold text-moss" data-testid="retreat-venue"><MapPin className="h-4 w-4" /> {L(r, "venue")}</p>
            <p className="mt-6 whitespace-pre-line leading-relaxed text-ink" data-testid="retreat-description">{L(r, "description")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {r.info_url && <a href={r.info_url} target="_blank" rel="noreferrer" className="btn-primary" data-testid="retreat-info-link">{t("retreat.tickets")} <ArrowUpRight className="h-4 w-4" /></a>}
              {digits && <a href={waLink(digits, `Hi! I saw "${r.title}" on Escamequita.com`)} target="_blank" rel="noreferrer" className="btn-outline" data-testid="retreat-whatsapp"><MessageCircle className="h-4 w-4" /> {t("retreat.contactHost")}</a>}
            </div>
            <div className="mt-8 border-t border-clay pt-6"><ShareButtons title={title} /></div>
          </div>
        </div>
      </section>
    </>
  );
}
